import React, { useEffect, useState } from 'react'
import LoadingIcon from '../../../../../../components/LoadingIcon'
import { PlanPositionRow } from '../../../../Plans/Positions/Edit/types'
import { Plan } from '../../../../Plans/types'
import { PlanPosition } from '../../../../Plans/Positions/types'

type Props = {
    theme: string
    contractPlanGuid: string | null
    open: boolean
    onClose: () => void
    onSelectRows: (rows: PlanPositionRow[]) => void
}

const ContractPositionAddFromPlanPopup: React.FC<Props> = ({
    theme,
    contractPlanGuid,
    open,
    onClose,
    onSelectRows,
}) => {
    const [loading, setLoading] = useState(false)
    const [planPositions, setPlanPositions] = useState<PlanPosition[]>([])
    const [rows, setRows] = useState<PlanPositionRow[]>([])
    const [selectedPositionGuid, setSelectedPositionGuid] = useState<
        string | null
    >(null)
    const [selectedRowGuids, setSelectedRowGuids] = useState<string[]>([])

    const loadPlanPositions = async () => {
        if (!contractPlanGuid) return
        setLoading(true)
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/api/backend/v1/plan_position/plan_guid/${contractPlanGuid}`,
                {
                    method: 'GET',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                }
            )
            if (!res.ok) throw new Error('Failed to fetch plan positions')
            const data = await res.json()
            setPlanPositions(Array.isArray(data) ? data : [])
        } catch (e) {
            console.error(e)
            setPlanPositions([])
        } finally {
            setLoading(false)
        }
    }

    const loadRows = async (planPositionGuid: string) => {
        setLoading(true)
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/api/backend/v1/plan_position_row/plan_pos_guid/${planPositionGuid}`,
                {
                    method: 'GET',
                    credentials: 'include',
                    headers: { 'Content-Type': 'application/json' },
                }
            )
            if (!res.ok) throw new Error('Failed to fetch plan rows')
            const data = await res.json()
            setRows(Array.isArray(data) ? data : [])
        } catch (e) {
            console.error(e)
            setRows([])
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        if (!open) return
        setSelectedPositionGuid(null)
        setSelectedRowGuids([])
        setRows([])
        loadPlanPositions()
    }, [open, contractPlanGuid])

    if (!open) return null

    return (
        <div className="fixed inset-0 z-50 bg-[#00000050] flex items-center justify-center">
            <div
                className="w-[1100px] max-w-[95vw] h-[700px] max-h-[90vh] rounded-lg bg-[#e5e5e5] dark:bg-[#262427] p-5 flex flex-col gap-4"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-center justify-between">
                    <h2 className="text-[16px] text-[#181818] dark:text-[#FFFFFF]">
                        Добавить позиции с плана
                    </h2>
                    <button
                        className="px-3 py-1 rounded bg-[#f6f6f6] dark:bg-[#494849] text-[#181818] dark:text-[#FFFFFF]"
                        onClick={onClose}
                    >
                        Закрыть
                    </button>
                </div>

                {!contractPlanGuid ? (
                    <div className="text-sm text-[#181818] dark:text-[#FFFFFF]">
                        У договора не заполнен `plan_guid`, поэтому добавить с
                        плана нельзя.
                    </div>
                ) : loading ? (
                    <div className="flex-1 flex items-center justify-center">
                        <LoadingIcon />
                    </div>
                ) : (
                    <div className="flex-1 min-h-0 grid grid-cols-2 gap-4">
                        <div className="border rounded p-2 overflow-y-auto">
                            <div className="text-sm mb-2 text-[#6A6A6A] dark:text-[#8A8A8A]">
                                Позиции плана
                            </div>
                            <div className="flex flex-col gap-1">
                                {planPositions.map((pos) => (
                                    <button
                                        key={pos.guid}
                                        className={`text-left px-3 py-2 rounded ${
                                            selectedPositionGuid === pos.guid
                                                ? 'bg-[#dbeafe] dark:bg-[#3b425a]'
                                                : 'bg-[#ffffff] dark:bg-[#2f2d30]'
                                        } text-[#181818] dark:text-[#FFFFFF]`}
                                        onClick={() => {
                                            setSelectedPositionGuid(pos.guid)
                                            setSelectedRowGuids([])
                                            loadRows(pos.guid)
                                        }}
                                    >
                                        <div className="text-xs">
                                            № {pos.ordinal_number}
                                        </div>
                                        <div className="text-sm">
                                            {pos.contract_subject}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="border rounded p-2 overflow-y-auto">
                            <div className="text-sm mb-2 text-[#6A6A6A] dark:text-[#8A8A8A]">
                                Строки выбранной позиции
                            </div>
                            <div className="flex flex-col gap-1">
                                {rows.map((row) => {
                                    const checked = selectedRowGuids.includes(
                                        row.guid
                                    )
                                    return (
                                        <label
                                            key={row.ordinal_number}
                                            className="flex gap-2 items-start px-3 py-2 rounded bg-[#ffffff] dark:bg-[#2f2d30] text-[#181818] dark:text-[#FFFFFF] cursor-pointer"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedRowGuids(
                                                            (prev) => [
                                                                ...prev,
                                                                row.guid,
                                                            ]
                                                        )
                                                    } else {
                                                        setSelectedRowGuids(
                                                            (prev) =>
                                                                prev.filter(
                                                                    (x) =>
                                                                        x !==
                                                                        row.guid
                                                                )
                                                        )
                                                    }
                                                }}
                                            />
                                            <div className="flex flex-col">
                                                <div className="text-sm">
                                                    {row.additional_info ||
                                                        row.okpd2_name ||
                                                        '-'}
                                                </div>
                                                <div className="text-xs text-[#6A6A6A] dark:text-[#8A8A8A]">
                                                    {row.okpd2_code
                                                        ? `${row.okpd2_code} - ${row.okpd2_name}`
                                                        : 'Без ОКПД2'}
                                                </div>
                                            </div>
                                        </label>
                                    )
                                })}
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex justify-end gap-2">
                    <button
                        className="px-4 py-2 rounded bg-[#f6f6f6] dark:bg-[#494849] text-[#181818] dark:text-[#FFFFFF]"
                        onClick={onClose}
                    >
                        Отмена
                    </button>
                    <button
                        className="px-4 py-2 rounded bg-[#83d2b7] dark:bg-[#187A33] text-[#181818] dark:text-[#FFFFFF]"
                        disabled={!selectedRowGuids.length}
                        onClick={() => {
                            const selectedRows = rows.filter((r) =>
                                selectedRowGuids.includes(r.guid)
                            )
                            onSelectRows(selectedRows)
                            onClose()
                        }}
                    >
                        Добавить
                    </button>
                </div>
            </div>
        </div>
    )
}

export default ContractPositionAddFromPlanPopup
